import SEO from '../components/SEO';
import ContactBox from '../components/ContactBox';
import Breadcrumbs from '../components/Breadcrumbs';
import { createBreadcrumbSchema, createAboutPageSchema } from '../lib/schema';
import { pagesData } from '../data';
import { Phone, Mail, CheckCircle2, Shield, Award, Users, ShieldCheck } from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/utils';

export default function About() {
  const page = pagesData["om-os"];

  if (!page) return <div className="py-40 text-center">Siden blev ikke fundet.</div>;

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Forside', url: 'https://janhedegaard.dk/' },
    { name: 'Om os', url: 'https://janhedegaard.dk/om-os' }
  ]);

  const aboutPageSchema = createAboutPageSchema();
  const schemaList = [aboutPageSchema, breadcrumbSchema];

  return (
    <>
      <SEO 
        title={page.title} 
        description={page.meta?.description || page.meta?.ogDescription}
        image={page.meta?.ogImage}
        schemaMarkup={schemaList}
      />

      {/* Hero */}
      <section className="relative min-h-[480px] py-24 md:py-28 flex items-center bg-stone-900">
        <div className="absolute inset-0">
          <img 
            src={getOptimizedImageUrl(page.meta.ogImage, 1600)} 
            alt={page.headline} 
            className="w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="mb-6">
            <Breadcrumbs items={[{ label: 'Om os', href: '/om-os' }]} isDark={true} />
          </div>
          <h1 className="text-[1.8rem] md:text-[2.4rem] font-bold text-white drop-shadow-lg max-w-4xl leading-[1.15]">
            {page.headline}
          </h1>
          <p className="text-xl text-stone-200 mt-6 max-w-2xl leading-relaxed drop-shadow-md">
            {page.subhead}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left Column: Verbatim Sections */}
            <div className="space-y-12">
              {page.sections.map((section, idx) => (
                <div key={idx} className="border-l-4 border-orange-600 pl-6 space-y-4">
                  <h2 className="text-2xl font-bold text-stone-900">{section.title}</h2>
                  {section.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} className="text-stone-600 leading-relaxed text-lg">
                      {p}
                    </p>
                  ))}
                </div>
              ))}

              {/* Kundetilfredshed & 3byggetilbud anmeldelser */}
              <div className="bg-stone-50 p-6 rounded-2xl border border-stone-200 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-lg" role="img" aria-label="Stjerne">⭐</span>
                </div>
                <div className="text-stone-700 text-base leading-relaxed">
                  <p className="font-bold text-stone-900 mb-1">Kundetilfredshed i top</p>
                  <p>
                    Vi vægter tilfredse kunder og ordentligt arbejde højt. Se vores seneste anmeldelser og bedømmelser på vores offentlige profil hos{' '}
                    <a
                      href="https://www.3byggetilbud.dk/leverandoer/entreprenr-jan-hedegaard-aps/"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-orange-600 font-semibold underline hover:text-orange-700 transition-colors"
                    >
                      3byggetilbud.dk
                    </a>
                    , hvor vi har 5 ud af 5 stjerner.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Images & Features */}
            <div className="space-y-8 lg:sticky lg:top-28">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { icon: Users, title: 'Erfarent Team', text: 'Vores medarbejdere har den rette faglige viden og kompetencer.' },
                  { icon: Shield, title: 'Autoriseret Kloakmester', text: 'Lovpligtig autorisation godkendt af Sikkerhedsstyrelsen (CVR 45789942).' },
                  { icon: Award, title: 'Kvalitetssikring', text: 'Lovpligtigt KLS-system og korrekt færdigmelding på alt autoriseret arbejde.' },
                  { icon: CheckCircle2, title: 'Troværdighed', text: 'Vi overholder alle aftaler, tidsplaner og myndighedskrav.' }
                ].map((item) => (
                  <div key={item.title} className="p-6 bg-stone-50 rounded-xl border border-stone-100 shadow-sm">
                    <item.icon className="w-8 h-8 text-orange-600 mb-4" />
                    <h3 className="font-bold text-stone-900 mb-2">{item.title}</h3>
                    <p className="text-stone-600 text-sm leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>

              {/* Autorisation & Virksomhedsoplysninger Boks */}
              <div className="p-6 bg-stone-900 text-white rounded-2xl border border-stone-800 shadow-lg space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-600 flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-lg text-white">Autorisation & Faglighed</p>
                    <p className="text-xs text-stone-400">Lovpligtig sikkerhed og kvalitetssikring</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-stone-800 text-sm">
                  <div>
                    <span className="text-xs text-stone-400 block">CVR-nummer:</span>
                    <span className="font-semibold text-stone-200">45789942</span>
                  </div>
                  <div>
                    <span className="text-xs text-stone-400 block">Autorisation:</span>
                    <span className="font-semibold text-stone-200">Aut. Kloakmester</span>
                  </div>
                  <div>
                    <span className="text-xs text-stone-400 block">Tilsynsmyndighed:</span>
                    <span className="font-semibold text-stone-200">Sikkerhedsstyrelsen</span>
                  </div>
                  <div>
                    <span className="text-xs text-stone-400 block">Kvalitet:</span>
                    <span className="font-semibold text-stone-200">Godkendt KLS & dokumentation</span>
                  </div>
                </div>
              </div>

              {/* Image Grid from Scrape */}
              <div className="grid grid-cols-2 gap-4 pt-4">
                {page.images.map((img, idx) => (
                  <div key={idx} className="aspect-[3/4] overflow-hidden rounded-xl bg-stone-100 shadow-md group">
                    <img 
                      src={getOptimizedImageUrl(img.src, 600)} 
                      alt={img.alt} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactBox 
            title="Lad os tage en snak om dit projekt"
            subtitle="Kontakt os i dag for professionel rådgivning og et uforpligtende tilbud."
          />
        </div>
      </section>
    </>
  );
}
